export interface CsvPatientRow {
    firstName:   string;
    lastName:    string;
    birthDate:   string;   // YYYY-MM-DD or ""
    gender:      string;
    salutation:  string;
    title:       string;
    street:      string;
    plz:         string;
    city:        string;
    tel:         string[];
    email:       string;
    isRecipient: boolean;
}

export interface CsvParseResult {
    rows:          CsvPatientRow[];
    rawHeaders:    string[];      // original header strings from file
    mappedFields:  Record<string, string>; // fieldName → matched header label
    unmappedHeaders: string[];   // headers that didn't match any field
}

// ── Column name aliases ───────────────────────────────────────────────────────
// All values are checked after normKey() (lowercase, strip non-alpha/digit/äöüß)

const ALIASES: Record<keyof Omit<CsvPatientRow, "isRecipient" | "tel">, string[]> = {
    firstName: [
        "vorname", "first_name", "firstname", "first name", "vname",
        "given name", "givenname", "rufname", "vornamen", "taufname",
        "pat.vorname", "patvorname", "patient.vorname", "p.vorname",
    ],
    lastName: [
        "nachname", "last_name", "lastname", "last name", "familienname",
        "nname", "surname", "family name", "familyname", "name",
        "pat.nachname", "patnachname", "patient.nachname", "patname",
        "patientnachname", "patientenname", "familname",
    ],
    birthDate: [
        "geburtsdatum", "birth_date", "birthdate", "dob", "geburtstag",
        "geb.datum", "geb_datum", "geboren", "date of birth", "gebdat",
        "gebdatum", "geb", "geburtsdaten", "geb.dat", "dateofbirth",
        "pat.geburtsdatum", "gebdatj", "gebdatjahr", "geb_dat",
    ],
    gender: [
        "geschlecht", "gender", "sex", "geschl", "geschlechtsangabe",
        "pat.geschlecht", "g", "gschl", "geschl.", "m/w/d",
    ],
    salutation: [
        "anrede", "salutation", "anredecode", "anr", "anredetext",
        "pat.anrede", "sal", "anrede.", "anredeform",
    ],
    title: [
        "titel", "title", "doktortitel", "academic_title", "grad",
        "akadem.grad", "akademischergrad", "akad.grad",
        "pat.titel", "ak.grad", "akadgrad", "doktgrad", "akadtitel",
    ],
    street: [
        "straße", "strasse", "street", "adresse", "address", "strase",
        "straße/nr", "strasse/nr", "anschrift", "str", "strassenname",
        "pat.strasse", "pat.straße", "wohnstrasse", "wohnstraße",
        "strassename", "strnum", "hausnummer", "strassehausnummer",
        "wohnungsadresse", "str.nr", "strnr", "strassennr",
    ],
    plz: [
        "plz", "postleitzahl", "zip", "postal_code", "postcode",
        "zip code", "pat.plz", "postlz", "postleitz", "postleit",
        "p.l.z", "plzort",
    ],
    city: [
        "stadt", "ort", "city", "wohnort", "gemeinde", "wohnstadt",
        "pat.ort", "ort/stadt", "ortschaft", "wohnortschaft", "ortsname",
        "ort/gemeinde", "locality", "wohngemeinde", "ortsangabe",
    ],
    email: [
        "email", "e-mail", "mail", "e_mail", "emailadresse",
        "email-adresse", "pat.email", "pat.e-mail",
        "emailaddress", "e.mail", "kontaktemail", "emailkontakt",
        "e-mailadresse", "emailadr",
    ],
};

const TEL_ALIASES = [
    "telefon", "tel", "phone", "mobile", "mobil", "handy",
    "telefonnummer", "tel.nr", "phone_number", "mobilnummer",
    "mobiltelefon", "telefon1", "telefon2", "mobile_phone", "cell",
    "cellphone", "rufnummer", "telefonnum", "tel1", "tel2",
    "mobil1", "mobil2", "handy1", "handy2", "mobilnr",
    "fon", "festnetz", "privat", "pat.telefon", "pat.mobil",
    "pat.handy", "pat.tel", "mobiltelefonnummer",
    "telephon", "telephonnummer", "natel", "natel1", "natel2",
    "mob", "tel_nr", "telnr", "fon1", "fon2", "telefonprivat",
    "telefonmobil", "telefonarbeit", "work_phone", "home_phone",
    "phone1", "phone2", "kontakttel", "tel.privat", "tel.mobil",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function normKey(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9äöüß]/g, "");
}

function matchAlias(key: string, aliases: string[]): boolean {
    for (const a of aliases) {
        const ak = normKey(a);
        if (ak === key) return true;
        // "pat.nachname" in file → key="patnachname", alias "nachname" → key ends with alias
        if (ak.length >= 4 && key.endsWith(ak)) return true;
        // "Name" in file → key="name", alias "nachname" → alias ends with key (risky – only if key>=5)
        // skipped intentionally to avoid false positives
    }
    return false;
}

function matchTel(key: string): boolean {
    return TEL_ALIASES.some((a) => {
        const ak = normKey(a);
        return ak === key || (ak.length >= 3 && key.startsWith(ak)) || (ak.length >= 3 && key.endsWith(ak));
    });
}

function detectSeparator(line: string): string {
    const counts: Record<string, number> = { ";": 0, ",": 0, "\t": 0, "|": 0 };
    for (const ch of line) {
        if (ch in counts) counts[ch]++;
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

function splitLine(line: string, sep: string): string[] {
    const result: string[] = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
            else inQuote = !inQuote;
        } else if (ch === sep && !inQuote) {
            result.push(cur.trim());
            cur = "";
        } else {
            cur += ch;
        }
    }
    result.push(cur.trim());
    return result;
}

function mapHeaders(headers: string[]): { colMap: Record<string, number>; mappedFields: Record<string, string> } {
    const colMap: Record<string, number> = {};
    const mappedFields: Record<string, string> = {};
    const telMatched = new Set<number>();

    headers.forEach((h, i) => {
        const key = normKey(h);

        for (const [field, aliases] of Object.entries(ALIASES)) {
            if (!(field in colMap) && matchAlias(key, aliases)) {
                colMap[field] = i;
                mappedFields[field] = h;
                return;
            }
        }

        if (matchTel(key) && !telMatched.has(i)) {
            const slot = "tel" in colMap ? `tel${Object.keys(colMap).filter((k) => k.startsWith("tel")).length}` : "tel";
            colMap[slot] = i;
            mappedFields[slot] = h;
            telMatched.add(i);
        }
    });

    return { colMap, mappedFields };
}

function parseDateDe(raw: string): string {
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m1 = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m1) return `${m1[3]}-${m1[2].padStart(2, "0")}-${m1[1].padStart(2, "0")}`;
    const m2 = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m2) return `${m2[3]}-${m2[2].padStart(2, "0")}-${m2[1].padStart(2, "0")}`;
    // YYYYMMDD compact
    const m3 = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (m3) return `${m3[1]}-${m3[2]}-${m3[3]}`;
    return "";
}

function normGender(raw: string): string {
    const v = raw.toLowerCase().trim();
    if (v === "m" || v === "männlich" || v === "male") return "M";
    if (v === "w" || v === "f" || v === "weiblich" || v === "female") return "F";
    if (v === "d" || v === "divers" || v === "diverse") return "D";
    return raw;
}

function normSalutation(raw: string): string {
    const v = raw.toLowerCase().trim();
    if (v === "herr" || v === "mr" || v === "h" || v === "1") return "H";
    if (v === "frau" || v === "mrs" || v === "ms" || v === "f" || v === "2") return "F";
    return raw;
}

// ── Value-based fallback detection ────────────────────────────────────────────
// Used when header-alias matching fails for email or tel columns.

function looksLikeEmail(v: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function looksLikePhone(v: string): boolean {
    const raw = v.trim();
    // Reject values that contain letters (insurance numbers, IDs, etc.)
    if (/[a-zA-Z]/.test(raw)) return false;
    const stripped = raw.replace(/[\s\-\(\)\/\|\+\.]/g, "");
    // All remaining chars must be digits, at least 6
    return /^\d{6,20}$/.test(stripped);
}

// Score each unmapped column by fraction of data rows where value satisfies predicate
function detectByValues(
    dataRows: string[][],
    unmappedIdxs: number[],
    predicate: (v: string) => boolean,
    threshold = 0.4,
): number | undefined {
    let best: number | undefined;
    let bestScore = 0;
    for (const idx of unmappedIdxs) {
        const filled = dataRows.filter((c) => c[idx]?.trim());
        if (filled.length === 0) continue;
        // For pipe-separated cells, split on | and check any sub-value
        const matches = filled.filter((c) =>
            c[idx].split("|").some((part) => predicate(part.trim())),
        ).length;
        const score = matches / filled.length;
        if (score >= threshold && score > bestScore) {
            bestScore = score;
            best = idx;
        }
    }
    return best;
}

// ── Public API ────────────────────────────────────────────────────────────────

export function parseCsvPatients(text: string): CsvParseResult {
    // Strip BOM (UTF-8 ﻿ or UTF-16 variants) + normalise line endings
    const cleaned = text.replace(/^﻿/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = cleaned.split("\n").filter((l) => l.trim() !== "");
    if (lines.length < 2) return { rows: [], rawHeaders: [], mappedFields: {}, unmappedHeaders: [] };

    const sep = detectSeparator(lines[0]);
    const rawHeaders = splitLine(lines[0], sep);
    const { colMap, mappedFields } = mapHeaders(rawHeaders);

    // ── Value-based fallback for email and tel ─────────────────────────────────
    // Parse all data rows as raw cell arrays for scanning
    const dataRows = lines.slice(1).map((l) => splitLine(l, sep));

    const mappedIdxSet = new Set(Object.values(colMap));
    const unmappedIdxs = rawHeaders.map((_, i) => i).filter((i) => !mappedIdxSet.has(i));

    if (!("email" in colMap)) {
        const fallbackEmail = detectByValues(dataRows, unmappedIdxs, looksLikeEmail);
        if (fallbackEmail !== undefined) {
            colMap.email = fallbackEmail;
            mappedFields.email = rawHeaders[fallbackEmail];
            mappedIdxSet.add(fallbackEmail);
        }
    }

    const hasTelCol = Object.keys(colMap).some((k) => k === "tel" || k.startsWith("tel"));
    if (!hasTelCol) {
        const remainingIdxs = rawHeaders.map((_, i) => i).filter((i) => !mappedIdxSet.has(i));
        const fallbackTel = detectByValues(dataRows, remainingIdxs, looksLikePhone, 0.5);
        if (fallbackTel !== undefined) {
            colMap.tel = fallbackTel;
            mappedFields.tel = rawHeaders[fallbackTel];
            mappedIdxSet.add(fallbackTel);
        }
    }

    const unmappedHeaders = rawHeaders.filter((_, i) => !mappedIdxSet.has(i));

    const telCols = Object.entries(colMap)
        .filter(([k]) => k === "tel" || k.startsWith("tel"))
        .map(([, i]) => i);

    // Split a single cell on | when | is not the column separator
    const splitCell = (raw: string): string[] => {
        if (!raw) return [];
        if (sep !== "|" && raw.includes("|")) {
            return raw.split("|").map((s) => s.trim()).filter(Boolean);
        }
        return [raw.trim()].filter(Boolean);
    };

    const rows: CsvPatientRow[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cells = splitLine(lines[i], sep);
        if (cells.every((c) => !c)) continue;

        const get = (field: string): string => {
            const idx = colMap[field];
            return idx !== undefined ? (cells[idx] ?? "").trim() : "";
        };

        const tels = [...new Set(
            telCols.flatMap((idx) => splitCell(cells[idx] ?? "")).filter(Boolean),
        )];

        const email  = splitCell(get("email"))[0] ?? "";
        const hasTel = tels.length > 0;

        rows.push({
            firstName:   get("firstName"),
            lastName:    get("lastName"),
            birthDate:   parseDateDe(get("birthDate")),
            gender:      normGender(get("gender")),
            salutation:  normSalutation(get("salutation")),
            title:       get("title"),
            street:      get("street"),
            plz:         get("plz"),
            city:        get("city"),
            tel:         tels,
            email,
            isRecipient: !!email || hasTel,
        });
    }

    return { rows, rawHeaders, mappedFields, unmappedHeaders };
}
